type ReadyCallback = (context: unknown) => void;

// Pre-IE9 event API, used only when addEventListener is missing.
interface LegacyEvents {
  attachEvent(type: string, listener: () => void): void;
  detachEvent(type: string, listener: () => void): void;
}

let readyList: { fn: ReadyCallback; ctx: unknown }[] = [];
let readyFired = false;
let readyEventHandlersInstalled = false;

// call this when the document is ready
// this function protects itself against being called more than once
function onReady() {
  if (!readyFired) {
    // this must be set to true before we start calling callbacks
    readyFired = true;
    for (let i = 0; i < readyList.length; i++) {
      // if a callback here happens to add new ready handlers,
      // the docReady() function will see that it already fired
      // and will schedule the callback to run right after
      // this event loop finishes so all handlers will still execute
      // in order and no new ones will be added to the readyList
      // while we are processing the list
      readyList[i].fn(readyList[i].ctx);
    }
    // allow any closures held by these functions to free
    readyList = [];

    if (readyEventHandlersInstalled) {
      if (document.removeEventListener) {
        document.removeEventListener("DOMContentLoaded", onReady);
        window.removeEventListener("load", onReady);
      } else {
        // must be IE
        (document as Document & LegacyEvents).detachEvent(
          "onreadystatechange",
          onReadyStateChange,
        );
        (window as typeof window & LegacyEvents).detachEvent("onload", onReady);
      }
      readyEventHandlersInstalled = false;
    }
  }
}

function onReadyStateChange() {
  if (document.readyState === "complete") {
    onReady();
  }
}

// This is the one public interface
// ready(fn, context);
// the context argument is optional - if present, it will be passed
// as an argument to the callback
export function ready(callback: ReadyCallback, context?: unknown) {
  // if ready has already fired, then just schedule the callback
  // to fire asynchronously, but right away
  if (readyFired) {
    setTimeout(function () {
      callback(context);
    }, 1);
    return;
  } else {
    // add the function and context to the list
    readyList.push({
      fn: callback,
      ctx: context,
    });
  }
  // if document already ready to go, schedule the ready function to run
  if (document.readyState === "complete") {
    setTimeout(onReady, 1);
  } else if (!readyEventHandlersInstalled) {
    // otherwise if we don't have event handlers installed, install them
    if (document.addEventListener) {
      // first choice is DOMContentLoaded event
      document.addEventListener("DOMContentLoaded", onReady);
      // backup is window load event
      window.addEventListener("load", onReady);
    } else {
      // must be IE
      (document as Document & LegacyEvents).attachEvent(
        "onreadystatechange",
        onReadyStateChange,
      );
      (window as typeof window & LegacyEvents).attachEvent("onload", onReady);
    }
    readyEventHandlersInstalled = true;
  }
}
